import React, { useState } from 'react'
import { useParentShipment } from '../context/ParentShipmentContext'
import moment from 'moment'
import StatusBadge from './StatusBadge'
import AdminDatePicker from './AdminDatePicker'
import UserControl from './UserControl'
import ShipperDocs from '../../Carrier/Components/ShipperDocs'
import ConsigneeDocs from '../../Carrier/Components/ConsigneeDocs'
import KwikTripConsigneeLocationInfo from '../../Carrier/Components/KwikTripConsigneeLocationInfo'
import KwikTripDropLiveCF10324 from '../../CommonSmallComponents/KwikTripDropLiveCF10324/KwikTripDropLiveCF10324'
import PalletLoadedCF10492 from '../../CommonSmallComponents/PalletLoadedCF10492'
import { ShipmentDetails2 } from '../../ShipmentDetails/ShipmentDetails2'

const CUSTOM_FIELDS_TO_DISPLAY = [212]

const CopyValue = ({ value, children }) => {
    const [toast, setToast] = useState(null)

    const copy = () => {
        navigator.clipboard.writeText(String(value)).then(() => {
            setToast('Copied!')
            setTimeout(() => setToast(null), 2000)
        })
    }

    return (
        <span onClick={copy} style={{ cursor: 'pointer' }}>
            {children}
            {toast && (
                <span className="ms-2 badge bg-secondary fw-normal" style={{ fontSize: '0.7rem' }}>
                    {toast}
                </span>
            )}
        </span>
    )
}

const formatDateRange = (d1, d2) => {
    if (!d1) return ''
    if (!d2 || d1 === d2) return moment(d1).format('MMM D, YYYY')
    return `${moment(d1).format('MMM D, YYYY')} – ${moment(d2).format('MMM D, YYYY')}`
}

const ContactInfo = ({ name, address, contact }) => (
    <div className="mt-2 small">
        {name && <div className="fw-semibold">{name}</div>}
        {address?.line1 && <div>{address.line1}{address.line2 ? ` ${address.line2}` : ''}</div>}
        {address?.city && <div>{address.city}, {address.state} {address.zip}</div>}
        {contact?.name && <div className="mt-1">{contact.name}</div>}
        {contact?.phone && (
            <div><a href={`tel:${contact.phone}`}>{contact.phone}</a></div>
        )}
        {contact?.email && (
            <div><a href={`mailto:${contact.email}`}>{contact.email}</a></div>
        )}
    </div>
)

const AppointmentPanel = ({
    shipment,
    isConsolidation,
    existingPickupAppts,
    existingDeliveryAppts,
    pickupRequired,
    deliveryRequired,
    pickupOrDelivery,
    setPickupOrDelivery,
    shipperLocationInfo,
    consigneeLocationInfo,
    adminDate,
    setAdminDate,
    reload,
    schedulingPickup,
    schedulingDelivery,
    showPickupInfo,
    showDeliveryInfo,
    stopType 
}) => {
    const { parentShipmentObject } = useParentShipment()
    const isKwikTrip = shipment.client.id === 'kwiktrip'
    const isMomcorpOrSleepcountry = shipment.client.id === 'momcorpX' || shipment.client.id === 'sleepcountry'

    const customFieldsToShow = (shipment.customFields || []).filter(
        f => CUSTOM_FIELDS_TO_DISPLAY.includes(f.fieldID)
    )

    return (
        <div className="p-3 h-100 border text-start" style={{ backgroundColor: '#F8F8F8' }}>

            <div className="pb-3 border-bottom mb-3">
                {!isConsolidation && (
                    <div className="mb-3">
                        <div className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '600' }}>BOL #</div>
                        <div className="fw-semibold"><CopyValue value={shipment.id}>{shipment.id}</CopyValue></div>
                    </div>
                )}

                <div className="mb-0">
                    <div className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '600' }}>PO/SO #</div>
                    <div className="fw-semibold">
                        <CopyValue value={shipment.usercontrol}>
                            <UserControl shipment={shipment} pickupOrDelivery={pickupOrDelivery} />
                        </CopyValue>
                    </div>
                </div>

                {isKwikTrip && (
                    <KwikTripConsigneeLocationInfo consigneeLocationInfo={consigneeLocationInfo} />
                )}

                {customFieldsToShow.length > 0 && (
                    <div className="mt-2 small">
                        {customFieldsToShow.map((f, i) => (
                            <div key={i}>
                                <span className="text-muted">{f.fieldLabel}:</span>{' '}
                                <span>{f.fieldValue}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Pickup ── */}
            {showPickupInfo && (
                <div className="mb-3">
                    <h5 className={`border-start border-3 ps-2 py-1 mb-2 rounded-end ${pickupOrDelivery === 'Pickup' ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary bg-light'}`}>
                        Pickup
                        {pickupRequired && deliveryRequired && (
                            pickupOrDelivery === 'Pickup'
                                ? <span className="badge bg-primary ms-2 fw-normal" style={{ fontSize: '0.7rem' }}>Selecting</span>
                                : <button
                                    className="btn btn-outline-primary btn-sm ms-2 py-0"
                                    onClick={() => setPickupOrDelivery('Pickup')}
                                >
                                    Schedule Pickup
                                </button>
                        )}
                    </h5>

                    <StatusBadge
                        type="Pickup"
                        existingAppts={existingPickupAppts}
                        required={pickupRequired}
                        locationInfo={shipperLocationInfo}
                        reload={reload}
                        isBeingScheduled={schedulingPickup}
                    />

                    {shipment.shipper?.dates?.available1 && (
                        <div className="small text-muted mt-1">
                            {pickupRequired ? 'Available: ' : ''}
                            {formatDateRange(
                                shipment.shipper.dates.available1,
                                shipment.shipper.dates.available2
                            )}
                        </div>
                    )}

                    <ContactInfo
                        name={shipment.shipper?.name}
                        address={shipment.shipper?.address}
                        contact={shipment.shipper?.contact}
                    />

                    <div className="mt-2">
                        <ShipperDocs shipment={shipment} />
                    </div>

                    {pickupRequired && !deliveryRequired && (
                        <div className="mt-2">
                            <ShipmentDetails2
                                shipment={shipment}
                                isConsolidation={isConsolidation}
                                pickupOrDelivery="Pickup"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* ── Delivery ── */}
            {showDeliveryInfo && (
                <div className="mb-3">
                    <hr/>
                    <h5 className={`border-start border-3 ps-2 py-1 mb-2 rounded-end ${pickupOrDelivery === 'Delivery' ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary bg-light'}`}>
                        Delivery
                        {deliveryRequired && pickupRequired && (
                            pickupOrDelivery === 'Delivery'
                                ? <span className="badge bg-primary ms-2 fw-normal" style={{ fontSize: '0.7rem' }}>Selecting</span>
                                : <button
                                    className="btn btn-outline-primary btn-sm ms-2 py-0"
                                    onClick={() => setPickupOrDelivery('Delivery')}
                                >
                                    Schedule Delivery
                                </button>
                        )}
                    </h5>

                    <StatusBadge
                        type="Delivery"
                        existingAppts={existingDeliveryAppts}
                        required={deliveryRequired}
                        locationInfo={consigneeLocationInfo}
                        reload={reload}
                        isBeingScheduled={schedulingDelivery}
                    />

                    {shipment.consignee?.dates?.requestedDeliveryDate && (
                        <div className="small text-muted mt-1">
                            {deliveryRequired ? 'Available: ' : ''}
                            {moment(shipment.consignee.dates.requestedDeliveryDate).format('MMM D, YYYY')}
                        </div>
                    )}

                    {shipment.consignee?.dates?.available1 && (
                        <div className="small text-muted">
                            {deliveryRequired ? 'Available Dates: ' : ''}
                            {formatDateRange(
                                shipment.consignee.dates.available1,
                                shipment.consignee.dates.available2
                            )}
                        </div>
                    )}

                    <AdminDatePicker shipment={shipment} setAdminDate={setAdminDate} />
                    <div className="col-12 col-md-6 px-0">
                        {isKwikTrip && <KwikTripDropLiveCF10324 role="quickSchedule" shipment={shipment} parentShipment={parentShipmentObject} stopType={stopType} />}
                    </div>
                    <ContactInfo
                        name={shipment.consignee?.name}
                        address={shipment.consignee?.address}
                        contact={shipment.consignee?.contact}
                    />
                    {isMomcorpOrSleepcountry && <PalletLoadedCF10492 role="carrier" shipment={shipment} />}

                    <div className="mt-2">
                        <ConsigneeDocs shipment={shipment} />
                    </div>

                    {((!pickupRequired && deliveryRequired) || (pickupRequired && deliveryRequired)) && (
                        <div className="mt-2">
                            <ShipmentDetails2
                                shipment={shipment}
                                isConsolidation={isConsolidation}
                                pickupOrDelivery="Delivery"
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default AppointmentPanel
